import { Layers } from "lucide-react";
import { PageHeading, Section, Field, Steps, Callout, RoleBadge, Diagram, MockRow, MockField, MockButton } from "../shared";

export default function AdminLanesSection() {
  return (
    <div data-testid="guide-admin-lanes">
      <PageHeading
        icon={Layers}
        title="Authority Lanes"
        description="A lane is the top-level category that decides how an action item is routed — for example the SDF MD Lane, the Above-MD Lane, or a Departmental Lane. The lane controls who reviews, who signs off, and whether an item gets dispatched upward."
      />

      <Callout variant="info" title="In plain words">
        A lane is like a track that work runs on. The track you put an item on decides who has to look at it and approve it. Setting up lanes is how you tell the system "items of this kind follow this approval path."
      </Callout>

      <Section title="Who can manage it">
        <div className="flex items-center gap-2"><RoleBadge role="super_admin" /><span className="text-xs text-white/55">Edits happen only on a <span className="text-white">draft</span> governance version.</span></div>
      </Section>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          Use it when you need a new routing category, or want to retire one. Remember: you always edit lanes inside a draft version, and a lane is useless until you assign actors (reviewers / sign-off authorities) to it.
        </p>
      </Section>

      <Section title="Adding a lane">
        <Diagram
          frameLabel="Admin → Lanes (on a draft version)"
          legend={[
            <>Click <span className="text-white">Add Lane</span>.</>,
            <>Enter a stable <span className="text-white">lane key</span> (e.g. SDF_MD_LANE) and a display name.</>,
            <>Set the <span className="text-white">sort order</span> for where it appears in dropdowns.</>,
            <>Click <span className="text-white">Save</span>, then assign actors on the Actors tab.</>,
          ]}
        >
          <MockButton label="Add Lane" marker={1} />
          <MockRow>
            <MockField label="Lane key" value="SDF_MD_LANE" marker={2} />
            <MockField label="Display name" value="SDF MD Lane" />
          </MockRow>
          <MockField label="Sort order" value="20" marker={3} />
          <MockButton label="Save" marker={4} />
        </Diagram>
        <Steps steps={[
          <>Open the active draft via Versions (clone Active first if no draft exists).</>,
          <>Lanes tab → <span className="text-white">Add Lane</span>.</>,
          <>Choose a lane key, display name, sort order. Save.</>,
          <>Switch to the Actors tab and assign reviewers / sign-off authorities for this lane (a lane with no actors cannot route items to anyone).</>,
          <>Activate the draft from Versions when ready.</>,
        ]} />
      </Section>

      <Section title="Lane fields">
        <div className="space-y-2">
          <Field name="Lane key" required="required"><p>Stable internal identifier (e.g. <span className="text-white">SDF_MD_LANE</span>). Once an item is registered against this lane key, do not rename it — the item carries the key forward as a foreign reference.</p></Field>
          <Field name="Display name" required="required"><p>Shown in the Register form and on detail pages.</p></Field>
          <Field name="Sort order" required="optional"><p>Order in dropdowns.</p></Field>
          <Field name="Active" required="optional"><p>Inactive lanes stop appearing in the Register form but historical items keep their lane reference.</p></Field>
        </div>
      </Section>

      <Callout variant="warning" title="Above-MD lanes have special behaviour">
        Lane keys recognised as Above-MD trigger the upward-dispatch flow: items there move from <span className="text-white">WITH_SDF_MD_REVIEW</span> → <span className="text-white">AWAITING_DISPATCH</span> → <span className="text-white">SENT_UPWARD</span>, with a working-day deadline the dispatcher must hit.
      </Callout>
    </div>
  );
}
